import { useCallback, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { DeleteAccountForm } from "./DeleteAccountForm";

interface DeleteAccountDialogProps {
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  errorMessage?: string;
  onDialogOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}

export function DeleteAccountDialog({
  onConfirm,
  isLoading,
  errorMessage,
  onDialogOpenChange,
  disabled = false,
}: DeleteAccountDialogProps) {
  const { t } = useTranslation("settings");
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onDialogOpenChange?.(nextOpen);
    },
    [onDialogOpenChange]
  );

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
      handleOpenChange(false);
    } catch (error) {
      // Keep dialog open when the mutation fails.
    }
  }, [onConfirm, handleOpenChange]);

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="destructive" disabled={disabled}>
          {t("deleteAccount.trigger")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("deleteAccount.dialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("deleteAccount.dialog.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {errorMessage && (
          <div className="p-3 mb-4 text-sm border rounded-md border-destructive/40 bg-destructive/10 text-destructive">
            {errorMessage}
          </div>
        )}
        <DeleteAccountForm
          onConfirm={handleConfirm}
          isLoading={isLoading}
          open={open}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}
